import React, { useContext, useEffect, useRef, useState } from "react";
import { Modal, Nav, Tab, Tabs } from "react-bootstrap";
import BookmarksTab from "./BookmarksTab";
import BookmarksAndNotesTab from "./BookmarksAndNotesTab";
import "./NavigationPanel.scss";
import ComboBox from "./ComboBox";
import { useFeature } from "../contexts/FeatureFlagContext";
import AnnotationsContext from "../contexts/AnnotationsContext";
import LiveRegion from "./LiveRegion";
import { focusModalCloseButton } from "../utils/DomUtils";

function preparePageData(pages) {
  // Prepare list of pages to be passed into the ComboBox component
  // Some books have no page number text, so they need to be filtered out with a truthiness check.
  // See title instance 1190835 (Velveteen Rabbit) for an example.
  // ComboBox expects an array of objects with a value property.
  const pageNumbers = pages.filter((x) => x).map((x) => ({ value: x }));

  // Prepare the placeholder text for the type-ahead input box
  const romanNumeralPages = pages.filter((x) => /^[MDCLXVImdclxvi]+$/.test(x));
  const numericPages = pages.filter((x) => /^[0-9]+$/.test(x));

  let placeholder =
    romanNumeralPages.length > 0
      ? `Pages ${romanNumeralPages[0]} to ${romanNumeralPages[romanNumeralPages.length - 1]}, ${numericPages[0]} to ${numericPages[numericPages.length - 1]}`
      : `Pages ${numericPages[0]} to ${numericPages[numericPages.length - 1]}`;

  return [pageNumbers, placeholder];
}

function NavigationPanel(props) {
  const triggeringElementRef = useRef(null);

  const onShow = () => {
    triggeringElementRef.current = document.activeElement;
    const currentTocElement = document.querySelector(
      "ol.tocNavList a.nav-link.active",
    );
    if (currentTocElement) {
      currentTocElement.scrollIntoView();
    }
  };

  const onEntered = () => {
    focusModalCloseButton(".navigationPanel");
  };

  const onExited = () => {
    if (triggeringElementRef.current) {
      triggeringElementRef.current.focus();
    }
  };

  const handleNavLinkClicked = (event, attribute) => {
    event.preventDefault();
    triggeringElementRef.current = null;
    props.onNavLinkClicked &&
      props.onNavLinkClicked(
        event.target.attributes.getNamedItem(attribute).value,
      );
  };

  const handlePageSelected = (page) => {
    const href = props.pageList.pageList.find(
      (x) => x.page === page.value,
    ).href;
    triggeringElementRef.current = null;
    props.onNavLinkClicked && props.onNavLinkClicked(props.baseUrl + href);
  };

  const handleTocLinkClicked = (event) => {
    handleNavLinkClicked(event, "href");
  };

  const handleBookmarkLinkClicked = (event) => {
    handleNavLinkClicked(event, "data-cfi");
  };

  const renderNavSublist = (tocItems, level) => {
    const _level = level || 1;

    const navItems = [];

    for (let tocItem of tocItems) {
      const subItems =
        tocItem.subitems.length > 0
          ? renderNavSublist(tocItem.subitems, _level + 1)
          : null;
      const isCurrentTocItem = tocItem === props.currentTocItem;

      const cssClasses = [];
      if (isCurrentTocItem) {
        cssClasses.push("text-light");
        cssClasses.push("bg-primary");
      }

      if (_level === 1 && subItems) {
        cssClasses.push("subhead");
      }

      navItems.push(
        <Nav.Item key={tocItem.id} as="li">
          <div>
            <Nav.Link
              onClick={handleTocLinkClicked}
              href={props.baseUrl + tocItem.href}
              className={cssClasses}
              active={isCurrentTocItem}
              aria-current={isCurrentTocItem ? "location" : null}
            >
              {tocItem.label}
            </Nav.Link>
          </div>
          {subItems}
        </Nav.Item>,
      );
    }
    return (
      <Nav as="ol" className="flex-column tocNavList">
        {navItems}
      </Nav>
    );
  };

  const nav = renderNavSublist(props.navigation ? props.navigation.toc : []);

  const [pages, placeholder] = preparePageData(props.pageList?.pages ?? []);

  const annotationsEnabled = useFeature("annotations");
  const annotations = useContext(AnnotationsContext);
  const [activeTab, setActiveTab] = useState(props.defaultTab || "toc");

  // While open, the panel renders its own announcement region inside the dialog,
  // so the provider's root region must stay silent. NVDA otherwise announces the
  // message from both regions (VoiceOver skips the root region while the modal
  // marks the rest of the app aria-hidden).
  const setLiveRegionActive = annotations?.setModalLiveRegionActive;
  useEffect(() => {
    if (!props.show || !setLiveRegionActive) return undefined;
    setLiveRegionActive(true);
    return () => setLiveRegionActive(false);
  }, [props.show, setLiveRegionActive]);

  return (
    <Modal
      show={props.show}
      onHide={props.onNavHide}
      onShow={onShow}
      onEntered={onEntered}
      onExited={onExited}
      scrollable={true}
      autoFocus={false}
      restoreFocus={false}
      aria-labelledby="navigationModalLabel"
      dialogClassName="navigationPanel__dialog"
      className="navigationPanel"
      backdropClassName="navigationPanel-backdrop"
    >
      <Modal.Header closeButton>
        <Modal.Title id="navigationModalLabel">
          <h2 className="h4">Book navigation</h2>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <LiveRegion
          announcing={annotations?.annotationAnnouncing}
          message={annotations?.annotationAnnouncementMessage}
        />
        <Tabs
          id="navigationPanelTabs"
          activeKey={activeTab}
          onSelect={(key) => key && setActiveTab(key)}
          justify
          className="navigationPanel__tabs"
        >
          <Tab eventKey="toc" title="Table of Contents">
            {pages.length > 0 ? (
              <div className="navigationPanel__goToPage">
                <ComboBox
                  isFocused={props.focusPage}
                  items={pages}
                  label="Go to Page: "
                  placeholder={placeholder}
                  onValueSelected={handlePageSelected}
                ></ComboBox>
              </div>
            ) : null}
            {nav}
          </Tab>
          {annotationsEnabled ? (
            <Tab eventKey="annotations" title="Bookmarks & Notes">
              <BookmarksAndNotesTab
                isActive={activeTab === "annotations"}
                annotationToScrollTo={props.annotationToScrollTo}
                onAnnotationLinkClicked={handleBookmarkLinkClicked}
                initialFilter={props.initialFilter}
              />
            </Tab>
          ) : (
            <Tab eventKey="bookmarks" title="Bookmarks">
              <BookmarksTab onBookmarkLinkClicked={handleBookmarkLinkClicked} />
            </Tab>
          )}
        </Tabs>
      </Modal.Body>
    </Modal>
  );
}

export default NavigationPanel;
